import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    quote: "CodiceCivile.AI ha trasformato il modo in cui ricerchiamo il diritto italiano. Quello che prima richiedeva ore ora richiede minuti.",
    author: "Marco Rossi",
    role: "Senior Partner",
    company: "Studio Legale Rossi & Associati",
    rating: 5
  },
  {
    quote: "L'integrazione MCP Server nel nostro workflow di sviluppo ha reso incredibilmente semplice accedere ai dati legislativi. Uno strumento essenziale.",
    author: "Laura Bianchi",
    role: "CTO",
    company: "LegalTech Solutions",
    rating: 5
  },
  {
    quote: "La qualità delle risposte dell'AI Expert è impressionante. Citazioni accurate e analisi approfondite che risparmiano tempo prezioso.",
    author: "Giuseppe Verdi",
    role: "Avvocato Civilista",
    company: "Studio Verdi",
    rating: 5
  },
  {
    quote: "Come ricercatore legale, apprezzo l'accuratezza e la velocità con cui posso accedere agli articoli del Codice Civile. Indispensabile.",
    author: "Francesca Romano",
    role: "Ricercatrice",
    company: "Università di Bologna",
    rating: 5
  },
  {
    quote: "Il supporto per GDPR e la conformità ai dati ci hanno dato la fiducia necessaria per integrare CodiceCivile.AI nei nostri processi aziendali.",
    author: "Antonio Conti",
    role: "Legal Compliance Officer",
    company: "FinanceGroup S.p.A.",
    rating: 5
  },
  {
    quote: "Finalmente uno strumento che comprende le sfumature del diritto civile italiano. Le risposte sono sempre pertinenti e ben documentate.",
    author: "Elena Martini",
    role: "Avvocato Associato",
    company: "Martini & Partners",
    rating: 5
  }
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused]);

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">
          Cosa Dicono i Nostri Clienti
        </h2>

        <div
          className="max-w-4xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="bg-white rounded-lg shadow-md p-8 md:p-12 relative">
            {/* Quote Icon */}
            <Quote className="absolute top-6 left-6 w-16 h-16 text-blue-600 opacity-20" />

            {/* Testimonial Content */}
            <div
              className={`transition-opacity duration-300 ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {/* Stars */}
              <div className="flex justify-center mb-6 relative z-10">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 text-yellow-400 fill-current"
                  />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-xl text-gray-700 text-center mb-8 italic relative z-10">
                "{currentTestimonial.quote}"
              </blockquote>

              {/* Author Info */}
              <div className="text-center relative z-10">
                <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-lg">
                    {currentTestimonial.author.charAt(0)}
                  </span>
                </div>
                <div className="font-semibold text-gray-900">
                  {currentTestimonial.author}
                </div>
                <div className="text-sm text-gray-600">
                  {currentTestimonial.role}
                </div>
                <div className="text-sm text-gray-500">
                  {currentTestimonial.company}
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={handlePrev}
              disabled={isTransitioning}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-full shadow-md transition-all"
              aria-label="Testimonianza precedente"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>

            <button
              onClick={handleNext}
              disabled={isTransitioning}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-full shadow-md transition-all"
              aria-label="Testimonianza successiva"
            >
              <ChevronRight className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center mt-6 gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isTransitioning) {
                    setIsTransitioning(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsTransitioning(false), 300);
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Vai alla testimonianza ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
